import { FC, Fragment, PropsWithChildren, ReactNode } from 'react';
import { Link, Outlet, To, useLocation } from 'react-router-dom';
import Confetti from 'react-confetti';

import { AppSidebar } from '@/components/app-sidebar';
import { AppTopNav } from '@/components/app-top-nav';
import { SpotlightManager } from '@/components/product-tour/spotlight-manager';
import {
  SidebarInset,
  SidebarProvider,
  SidebarMobileTrigger,
} from '@/components/ui/sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { cn } from '@/lib/utils';
import NotificationBar from '@/components/notification-bar';
import { APP_ROUTE } from '@/constants';
import { UserContainer } from '@/providers';
import DemoModeBanner, { isDemoMode } from '@/components/demo-mode-banner';
import { DemoModeNudgeNotification } from '@/components/demo-mode-nudge-notification';
import McpAnnouncement from '@/components/mcp-announcement';
import { DiscountBanner } from '@/components/banner/DiscountBanner';
import { usePlanLimits } from '@/hooks/use-plan-limits';
import { isLimitEnforcementAllowedRoute } from '@/utils/route-helpers';
import { LimitEnforcementModal } from '@/components/limit-enforcement-modal';
import VerifyEmailTopbar, { isVerified } from '../auth/VerifyEmailTopbar';

export const NewHomeBreadcrumb: FC<{
  links: Array<{ label: ReactNode; url: To }>;
}> = ({ links }) => (
  <Breadcrumb className="flex-1 min-w-0">
    <BreadcrumbList className="max-w-full overflow-x-hidden flex-nowrap">
      {links.map((l, index) => (
        <Fragment key={index}>
          <BreadcrumbItem
            className={cn(
              'text-base font-semibold text-grayscale-600 truncate',
              index === links.length - 1 && 'text-grayscale-900'
            )}
          >
            <BreadcrumbLink asChild>
              <Link to={l.url} className="truncate">
                {l.label}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          {!(index === links.length - 1) && <BreadcrumbSeparator />}
        </Fragment>
      ))}
    </BreadcrumbList>
  </Breadcrumb>
);

export function NewHomeLayoutTopNav({ children }: PropsWithChildren) {
  return <AppTopNav>{children}</AppTopNav>;
}

export function NewHomeLayoutContent({ children }: PropsWithChildren) {
  return (
    <div className="flex-1 flex flex-col min-h-0 flex-shrink-1">{children}</div>
  );
}

const NewHomeLayout = () => {
  const { state: userState } = UserContainer.useContainer();
  /**
   * NEW CODE
   */
  const location = useLocation();
  const { shouldCelebrate } = location.state ?? {}; // only exists after onboarding flow
  /**
   * END NEW CODE
   */

  const isAddonRoute = location.pathname.includes(APP_ROUTE.ADDON);
  const isDemo = isDemoMode(userState);
  const verified = isVerified(userState);

  // Limit enforcement
  const { isOverLimit, planName } = usePlanLimits();
  const shouldShowEnforcementModal =
    isOverLimit &&
    !isLimitEnforcementAllowedRoute(location.pathname) &&
    !isAddonRoute &&
    !isDemo;

  if (isAddonRoute) {
    return <Outlet />;
  }

  return (
    <div className="w-svw font-sans h-svh">
      <div className="max-w-screen-2xl w-full 2xl:border-x mx-auto h-full flex flex-col">
        {/* Discount Banner */}
        <DiscountBanner />

        {/* Verify Email Banner (existing) */}
        <VerifyEmailTopbar />

        {/* Demo Mode Banner */}
        <DemoModeBanner />

        <SidebarProvider className="flex-1 min-h-0">
          {/* SIDEBAR */}
          <AppSidebar />

          {/* MOBILE TRIGGER */}
          <SidebarMobileTrigger />

          {/* INSET */}
          <SidebarInset className="overflow-y-auto">
            <Outlet />
          </SidebarInset>
        </SidebarProvider>
      </div>

      {/* {shouldCelebrate && (
        <Confetti
          width={window.innerWidth}
          height={window.innerHeight}
          numberOfPieces={1_000}
          recycle={false}
          style={{
            zIndex: 99, // higher than the sidebar
          }}
        />
      )} */}

      <NotificationBar />
      <DemoModeNudgeNotification />
      <McpAnnouncement suppressed={shouldShowEnforcementModal} />
      <SpotlightManager />
      <LimitEnforcementModal
        open={shouldShowEnforcementModal}
        currentPlan={planName}
      />
    </div>
  );
};

NewHomeLayout.displayName = 'NewHomeLayout';

export default NewHomeLayout;
